import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  createCategory, 
  updateCategory, 
  getCategoryDetail, 
  getCategories,
  getAvailableIcons 
} from '../../services/api';
import {
  FiArrowLeft,
  FiSave,
  FiX,
  FiFolder,
  FiPackage,
  FiEye,
  FiUpload,
  FiImage,
  FiLayers,
  FiHash,
  FiTag,
  FiChevronDown,
  FiSearch,
  FiCheck,
  FiAlertCircle,
  FiCheckCircle,
  FiGrid,
  FiList,
  FiStar,
  FiSettings,
  FiType,
  FiRefreshCw
} from 'react-icons/fi';

function CategoryForm({ admin }) {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(categoryId);
  
  // Get parent_id from query params for creating subcategory
  const queryParams = new URLSearchParams(location.search);
  const parentIdFromQuery = queryParams.get('parent_id');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [icons, setIcons] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLibrary, setSelectedLibrary] = useState('Ionicons');
  const [iconViewMode, setIconViewMode] = useState('grid'); // grid or list
  const [formData, setFormData] = useState({
    name: '',
    parent_id: parentIdFromQuery || '',
    icon_name: 'folder',
    icon_library: 'Ionicons',
    icon_color: '#3B82F6',
    background_color: '#1F2937',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchInitialData();
  }, [categoryId, parentIdFromQuery]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch available icons
      const iconsResponse = await getAvailableIcons();
      if (iconsResponse.success) {
        setIcons(iconsResponse.data);
        // Set default icon library if available
        if (iconsResponse.data.libraries && iconsResponse.data.libraries.length > 0) {
          setSelectedLibrary(iconsResponse.data.libraries[0]);
          setFormData(prev => ({ ...prev, icon_library: iconsResponse.data.libraries[0] }));
        }
      }

      // Fetch categories for parent selection
      const categoriesResponse = await getCategories({ include_subcategories: false });
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }

      // If editing, fetch category details
      if (isEdit) {
        const categoryResponse = await getCategoryDetail(categoryId);
        if (categoryResponse.success) {
          const category = categoryResponse.data;
          setFormData({
            name: category.name || '',
            parent_id: category.parent_id || '',
            icon_name: category.icon_name || 'folder',
            icon_library: category.icon_library || 'Ionicons',
            icon_color: category.icon_color || '#3B82F6',
            background_color: category.background_color || '#1F2937',
            description: category.description || '',
            is_active: category.is_active !== false
          });
          setSelectedLibrary(category.icon_library || 'Ionicons');
        } else {
          throw new Error(categoryResponse.message || 'Failed to load category');
        }
      }
    } catch (err) {
      setError('Failed to load form data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const submitData = {
        ...formData,
        parent_id: formData.parent_id || null,
        icon_name: formData.icon_name || 'folder'
      };

      const response = isEdit 
        ? await updateCategory(categoryId, submitData)
        : await createCategory(submitData);

      if (response.success) {
        setSuccessMessage(isEdit ? 'Category updated successfully!' : 'Category created successfully!');
        
        // Redirect after short delay
        setTimeout(() => {
          navigate('/admin/categories');
        }, 1500);
      } else {
        throw new Error(response.message || `${isEdit ? 'Update' : 'Creation'} failed`);
      }
    } catch (err) {
      setError(`Failed to ${isEdit ? 'update' : 'create'} category: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getParentCategories = () => {
    return categories.filter(cat => !cat.parent_id && cat.category_id !== (isEdit ? parseInt(categoryId) : null));
  };

  const getFilteredIcons = () => {
    if (!icons?.categories) return [];
    
    let allIcons = [];
    Object.values(icons.categories).forEach(categoryIcons => {
      allIcons = [...allIcons, ...categoryIcons];
    });

    if (searchTerm) {
      return allIcons.filter(icon => 
        icon.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return allIcons;
  };

  const handleColorChange = (field, value) => {
    // Ensure color value is valid
    if (value.startsWith('#')) {
      handleChange(field, value);
    } else if (value) {
      handleChange(field, '#' + value.replace(/[^0-9A-Fa-f]/g, ''));
    }
  };

  const getParentCategoryName = () => {
    if (!formData.parent_id) return 'Main Category';
    const parent = categories.find(cat => cat.category_id == formData.parent_id);
    return parent ? parent.name : 'Unknown Category';
  };

  const presetColors = {
    icon: [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
      '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ],
    background: [
      '#1F2937', '#111827', '#374151', '#4B5563', '#6B7280',
      '#1E40AF', '#047857', '#92400E', '#7C3AED', '#BE185D'
    ]
  };

  // Loading Skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-700 rounded w-1/4 mb-2"></div>
                  <div className="h-10 bg-gray-700 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">
                {isEdit ? 'Edit Category' : 'Create Category'}
              </span>
            </h1>
            <p className="text-gray-400 mt-1">
              {isEdit 
                ? 'Update category details, appearance, and settings'
                : formData.parent_id 
                  ? `Add subcategory to "${getParentCategoryName()}"`
                  : 'Create a new product category'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to="/admin/categories"
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors font-medium flex items-center"
            >
              <FiArrowLeft className="mr-2" />
              Back
            </Link>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Form Fields */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information Card */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center mb-6">
                  <FiSettings className="text-[#ff660e] mr-2" />
                  <h3 className="text-lg font-medium text-white">Basic Information</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                      <FiTag className="mr-2" />
                      Category Name *
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter category name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                      <FiType className="mr-2" />
                      Description (Optional)
                    </label>
                    <textarea
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 min-h-[100px]"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Brief description of this category..."
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                      <FiLayers className="mr-2" />
                      Parent Category
                    </label>
                    <div className="relative">
                      <select
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white appearance-none"
                        value={formData.parent_id}
                        onChange={(e) => handleChange('parent_id', e.target.value)}
                      >
                        <option value="">Main Category (Top Level)</option>
                        {getParentCategories().map(cat => (
                          <option key={cat.category_id} value={cat.category_id}>
                            {cat.name} {cat.parent_id ? '(Subcategory)' : ''}
                          </option>
                        ))}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    {formData.parent_id && (
                      <p className="text-sm text-gray-400 mt-2 flex items-center">
                        <FiCheck className="mr-1 text-green-400" />
                        This will be a subcategory of "{getParentCategoryName()}"
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Icon Selection Card */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <FiImage className="text-[#ff660e] mr-2" />
                    <h3 className="text-lg font-medium text-white">Icon Selection</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setIconViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        iconViewMode === 'grid'
                          ? 'bg-[#ff660e] text-white'
                          : 'bg-gray-700 text-gray-400 hover:text-white'
                      }`}
                    >
                      <FiGrid />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIconViewMode('list')}
                      className={`p-2 rounded-lg transition-colors ${
                        iconViewMode === 'list'
                          ? 'bg-[#ff660e] text-white'
                          : 'bg-gray-700 text-gray-400 hover:text-white'
                      }`}
                    >
                      <FiList />
                    </button>
                  </div>
                </div>

                {/* Icon Library Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Icon Library
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {icons?.libraries?.map(library => (
                      <button
                        key={library}
                        type="button"
                        onClick={() => {
                          setSelectedLibrary(library);
                          handleChange('icon_library', library);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          selectedLibrary === library
                            ? 'bg-[#ff660e] text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {library}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Icon Search */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Search Icons
                  </label>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                      placeholder="Search icons by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Icons Grid/List */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Select Icon
                  </label>
                  {iconViewMode === 'grid' ? (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                      {getFilteredIcons().slice(0, 48).map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => handleChange('icon_name', icon)}
                          className={`aspect-square p-3 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                            formData.icon_name === icon
                              ? 'border-[#ff660e] bg-[#ff660e]/10 shadow-lg'
                              : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                          }`}
                        >
                          <div 
                            className="text-2xl mb-1 flex items-center justify-center"
                            style={{ color: formData.icon_color }}
                          >
                            {icon}
                          </div>
                          <div className="text-xs text-gray-400 truncate">{icon}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                      {getFilteredIcons().slice(0, 30).map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => handleChange('icon_name', icon)}
                          className={`w-full p-3 rounded-lg border transition-all duration-200 hover:bg-gray-700/50 flex items-center justify-between ${
                            formData.icon_name === icon
                              ? 'border-[#ff660e] bg-[#ff660e]/10'
                              : 'border-gray-600 hover:border-gray-500'
                          }`}
                        >
                          <div className="flex items-center">
                            <div 
                              className="text-2xl mr-3"
                              style={{ color: formData.icon_color }}
                            >
                              {icon}
                            </div>
                            <span className="text-sm text-gray-300">{icon}</span>
                          </div>
                          {formData.icon_name === icon && (
                            <FiCheck className="text-[#ff660e]" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {getFilteredIcons().length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-600 text-4xl mb-3">🔍</div>
                      <p className="text-gray-400">No icons found matching "{searchTerm}"</p>
                    </div>
                  )}
                </div>

                {/* Selected Icon Preview */}
                <div className="bg-gray-700/30 rounded-xl p-6 border border-gray-600">
                  <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center">
                    <FiEye className="mr-2" />
                    Selected Icon Preview
                  </h4>
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-xl mb-4"
                      style={{ backgroundColor: formData.background_color }}
                    >
                      <span 
                        className="text-4xl"
                        style={{ color: formData.icon_color }}
                      >
                        {formData.icon_name}
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-white font-medium">{formData.icon_name}</div>
                      <div className="text-sm text-gray-400 mt-1">{formData.icon_library}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Color Selection & Actions */}
            <div className="space-y-6">
              {/* Color Selection Card */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center mb-6">
                  <FiPackage className="text-[#ff660e] mr-2" />
                  <h3 className="text-lg font-medium text-white">Color Scheme</h3>
                </div>

                {/* Icon Color */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Icon Color
                  </label>
                  <div className="mb-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="color"
                        className="w-12 h-12 border border-gray-600 rounded-lg cursor-pointer bg-transparent"
                        value={formData.icon_color}
                        onChange={(e) => handleColorChange('icon_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white font-mono"
                        value={formData.icon_color}
                        onChange={(e) => handleColorChange('icon_color', e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {presetColors.icon.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => handleChange('icon_color', color)}
                          className="aspect-square rounded-lg border border-gray-600 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          title={color}
                        >
                          {formData.icon_color === color && (
                            <FiCheck className="text-white mx-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Background Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Background Color
                  </label>
                  <div className="mb-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="color"
                        className="w-12 h-12 border border-gray-600 rounded-lg cursor-pointer bg-transparent"
                        value={formData.background_color}
                        onChange={(e) => handleColorChange('background_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white font-mono"
                        value={formData.background_color}
                        onChange={(e) => handleColorChange('background_color', e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {presetColors.background.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => handleChange('background_color', color)}
                          className="aspect-square rounded-lg border border-gray-600 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          title={color}
                        >
                          {formData.background_color === color && (
                            <FiCheck className="text-white mx-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Actions Card */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center mb-6">
                  <FiSettings className="text-[#ff660e] mr-2" />
                  <h3 className="text-lg font-medium text-white">Settings & Actions</h3>
                </div>

                {/* Status Toggle */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Category Status
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleChange('is_active', !formData.is_active)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:ring-offset-2 focus:ring-offset-gray-800 ${
                        formData.is_active ? 'bg-green-600' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="ml-3 text-sm text-gray-300">
                      {formData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {formData.is_active 
                      ? 'Category will be visible to users'
                      : 'Category will be hidden from users'}
                  </p>
                </div>

                {/* Form Actions */}
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full px-4 py-3 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center justify-center hover:scale-[1.02]"
                  >
                    {saving ? (
                      <>
                        <FiRefreshCw className="animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiSave className="mr-2" />
                        {isEdit ? 'Update Category' : 'Create Category'}
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/admin/categories')}
                    className="w-full px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium flex items-center justify-center"
                  >
                    <FiX className="mr-2" />
                    Cancel
                  </button>
                </div>

                {/* Live Preview */}
                <div className="mt-6 pt-6 border-t border-gray-600">
                  <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center">
                    <FiEye className="mr-2" />
                    Live Preview
                  </h4>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center shadow-md"
                        style={{ backgroundColor: formData.background_color }}
                      >
                        <span 
                          className="text-xl"
                          style={{ color: formData.icon_color }}
                        >
                          {formData.icon_name}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">
                          {formData.name || 'Category Name'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formData.parent_id ? 'Subcategory' : 'Main Category'}
                        </div>
                      </div>
                    </div>
                    {formData.description && (
                      <p className="text-xs text-gray-500 mt-3">
                        {formData.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        /* Custom scrollbar for icon list */
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #374151;
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #6B7280;
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
      `}</style>
    </div>
  );
}

export default CategoryForm;